import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { tagsRepository } from '../repositories/tags_repository'
import { unitsRepository } from '../repositories/units_repository'
import { homepageDocument } from '../seo/homepage_seo.js'
import { Image, Unit } from '../types'
import '../stylesheets/HomeSeo.scss'

const FEATURED_LIMIT = 6

const group = (id: string) => {
  const found = homepageDocument.groups.find(item => item.id === id)
  if (!found) throw new Error('Missing homepage group: ' + id)
  return found
}

const categoryId = (href: string): number | undefined => {
  const match = /^\/categories\/(\d+)$/.exec(href)
  if (!match) return undefined
  return Number(match[1])
}

// Same sources as category browse: set pack art (tags.images thumb2x),
// then the catalog card image the set grid shows when the tag has no art.
const catalogImageSrc = (images?: Image[]): string | undefined => {
  if (!images || images.length === 0) return undefined
  const image = images.find(item => !item.sprite && (item.thumb2x || item.imageUrl))
    ?? images.find(item => item.thumb2x || item.imageUrl)
  if (!image) return undefined
  return image.thumb2x || image.imageUrl
}

const HomeSeo = () => {
  const location = useLocation()
  const isHome = location.pathname === '/'
  const [listings, setListings] = useState<Unit[]>([])
  const [market, setMarket] = useState<'loading' | 'ready' | 'fallback'>('loading')
  const [setImages, setSetImages] = useState<Record<string, string>>({})

  useEffect(() => {
    if (!isHome) return undefined

    let cancelled = false
    setMarket('loading')
    setListings([])

    unitsRepository.index({
      platform: 'holic',
      game: [1],
      sold: 'for_sale',
      sort: 'price_desc',
      page: 1,
      limit: FEATURED_LIMIT,
    }).then(({ listings: next }) => {
      if (cancelled) return
      const featured = (next ?? []).filter(unit => unit.id && unit.userId).slice(0, FEATURED_LIMIT)
      if (featured.length === 0) {
        setMarket('fallback')
        return
      }
      setListings(featured)
      setMarket('ready')
    }).catch(() => {
      if (!cancelled) setMarket('fallback')
    })

    return () => {
      cancelled = true
    }
  }, [isHome])

  useEffect(() => {
    if (!isHome) return undefined

    let cancelled = false
    const links = group('sets').links
    const ids = links.map(link => categoryId(link.href)).filter((id): id is number => typeof id === 'number')

    const load = async () => {
      let tags: { id: number, images: Image[] }[] = []
      try {
        const response = await tagsRepository.index({
          params: {
            tagIds: ids,
            page: 1,
            limit: ids.length,
          },
        })
        tags = response.tags ?? []
      } catch {
        tags = []
      }

      const byId = new Map(tags.map(tag => [tag.id, tag]))
      const entries = await Promise.all(links.map(async link => {
        const id = categoryId(link.href)
        if (!id) return [link.href, undefined] as const
        const fromTag = catalogImageSrc(byId.get(id)?.images)
        if (fromTag) return [link.href, fromTag] as const
        try {
          const { items } = await tagsRepository.getItems(id, { page: 1, limit: 1, types: [] })
          return [link.href, catalogImageSrc(items?.[0]?.images)] as const
        } catch {
          return [link.href, undefined] as const
        }
      }))

      if (cancelled) return
      const next: Record<string, string> = {}
      entries.forEach(([href, src]) => {
        if (src) next[href] = src
      })
      setSetImages(next)
    }

    load().catch(() => {
      if (!cancelled) setSetImages({})
    })

    return () => {
      cancelled = true
    }
  }, [isHome])

  if (!isHome) return null

  const doc = homepageDocument
  const hubs = group('hubs')
  const sets = group('sets')
  const forSale = group('forSale')
  const guide = group('guide')
  const primary = forSale.links[0]
  const condition = guide.links.find(link => link.href === '/about/condition') ?? guide.links[0]
  const chips = [...hubs.links, primary]
  const visibleTrust = doc.trust.filter(point => point.home !== false)

  const setTile = (link: { href: string, label: string }, className?: string) => {
    const src = setImages[link.href]
    return (
      <Link className={ className } to={ link.href }>
        { src ? <img src={ src } alt="" /> : null }
        <span>{ link.label }</span>
      </Link>
    )
  }

  return (
    <section id="home-seo" lang="ja" className="home-seo">
      <div className="home-seo-hero">
        <h1>{ doc.h1 }</h1>
        <p className="home-seo-lead">{ doc.lead }</p>
        <div className="home-seo-ctas">
          <Link className="home-seo-cta home-seo-cta-primary" to={ primary.href }>{ doc.shop.primaryLabel }</Link>
          <Link className="home-seo-cta home-seo-cta-secondary" to={ doc.shop.secondaryHref }>{ doc.shop.secondaryLabel }</Link>
          <Link className="home-seo-cta home-seo-cta-tertiary" to={ condition.href }>{ doc.shop.tertiaryLabel }</Link>
        </div>
      </div>
      <div className="home-seo-market" aria-label={ doc.shop.marketLabel } aria-busy={ market === 'loading' }>
        <p className="home-seo-kicker">{ doc.shop.marketLabel }</p>
        <ul className="home-seo-entries">
          { market === 'ready'
            ? listings.map(unit => {
              const listing = unit.listings?.find(item => (!item.platform || item.platform === 'holic') && !item.soldAt) ?? unit.listings?.[0]
              const image = unit.images?.[0] ?? unit.items?.[0]?.images?.[0]
              const src = image?.thumb2x || image?.imageUrl
              const name = unit.items?.[0]?.nameJa || unit.items?.[0]?.nameEn || '出品'
              const price = listing && typeof listing.priceJpy === 'number' ? listing.priceJpy : unit.priceJpy
              return (
                <li key={ unit.id }>
                  <Link className="home-seo-listing" to={ `/u/${String(unit.userId)}/units/${String(unit.id)}` }>
                    { src ? <img src={ src } alt="" /> : <span className="home-seo-placeholder" aria-hidden="true" /> }
                    <span className="home-seo-card-name">{ name }</span>
                    { typeof price === 'number' ? <span className="home-seo-card-price">{ price.toLocaleString('ja-JP') }円</span> : null }
                  </Link>
                </li>
              )
            })
            : market === 'loading'
              ? Array.from({ length: FEATURED_LIMIT }, (_, index) => (
                <li key={ index } className="home-seo-skeleton" aria-hidden="true" />
              ))
              : sets.links.slice(0, FEATURED_LIMIT).map(link => (
                <li key={ `entry-${link.href}` }>
                  { setTile(link, 'home-seo-entry') }
                </li>
              )) }
        </ul>
      </div>
      <nav className="home-seo-hubs" aria-label={ doc.shop.hubLabel }>
        <p className="home-seo-kicker">{ doc.shop.hubLabel }</p>
        <ul className="home-seo-chips">
          { chips.map(link => (
            <li key={ link.href }>
              <Link to={ link.href }>{ link.chip ?? link.label }</Link>
            </li>
          )) }
        </ul>
      </nav>
      <nav className="home-seo-sets" aria-label={ sets.heading }>
        <h2>{ sets.heading }</h2>
        <ul className="home-seo-tiles">
          { sets.links.map(link => (
            <li key={ link.href }>
              { setTile(link) }
            </li>
          )) }
        </ul>
      </nav>
      <div className="home-seo-below">
        { doc.intro.map(paragraph => <p key={ paragraph }>{ paragraph }</p>) }
        <ul className="home-seo-trust">
          { visibleTrust.map(point => (
            <li key={ point.title }>
              <strong>{ point.title }</strong> { point.body }
            </li>
          )) }
        </ul>
        <nav className="home-seo-guides" aria-label={ guide.heading }>
          <ul className="home-seo-legal">
            { guide.links.map(link => (
              <li key={ link.href }>
                <Link to={ link.href }>{ link.label }</Link>
              </li>
            )) }
          </ul>
        </nav>
      </div>
    </section>
  )
}

export { HomeSeo }
